/**
 * live-preview-tunnel, app side (framework-free; the React frame and the e2e harness share it).
 *
 * 1. `openPreviewSession`: a fresh ECDH key pair, the device-signed sealed `preview.open`
 *    command, and the tunnel keys derived from the machine's reply. Nothing but ciphertext and ids
 *    ever reaches the hub.
 * 2. `attachPreviewFrame`: waits for *that* iframe (identity + exact preview origin) to announce
 *    itself, then hands it this preview's keys (non-extractable CryptoKeys) and a MessagePort, and
 *    relays the port's ciphertext frames to and from the hub. Account keys never leave this origin.
 */
import {
  derivePreviewTunnelKeys,
  fromBase64Url,
  generatePreviewEphemeral,
  type PreviewTunnelKeys,
  toBase64Url,
} from '@viko/crypto'
import { type CommandPayload, PreviewOpenResult, validPreviewHostSuffix } from '@viko/protocol'

export interface OpenedPreview {
  previewId: string
  label: string
  /** The preview's own origin, e.g. `https://p-…--pv.viko.build`. */
  origin: string
  keys: PreviewTunnelKeys
  /** Path + query + #hash to open first (deep links and hash routes preserved). */
  entry: string
  target: { origin: string; port: number }
  machineLabel: string
  access: 'run' | 'shared'
  expiresAt: number
}

export type OpenOutcome =
  | { status: 'open'; session: OpenedPreview }
  | { status: 'needs_share'; port: number; process: string }
  | { status: 'denied'; reason: string }

export interface PreviewHostConfig {
  /** Appended to the label: `--pv.viko.build`, `--pvs.viko.build`, `.localhost:8788`. */
  suffix: string
  /** `https:` in production; `http:` only for `*.localhost` development. */
  scheme: 'https:' | 'http:'
}

/** Build-time preview host config, or undefined when live previews are not provisioned. */
export function previewHostConfig(suffix: string | undefined): PreviewHostConfig | undefined {
  const s = validPreviewHostSuffix(suffix)
  if (!s) return undefined
  const local = /\.localhost(?::\d+)?$/.test(s)
  return { suffix: s, scheme: local ? 'http:' : 'https:' }
}

export function entryOf(url: string): string {
  const u = new URL(url)
  return `${u.pathname || '/'}${u.search}${u.hash}`
}

export async function openPreviewSession(o: {
  url: string
  runId?: string
  projectId?: string
  accountId: string
  machineId: string
  host: PreviewHostConfig
  open: (payload: CommandPayload<'preview.open'>) => Promise<unknown>
}): Promise<OpenOutcome> {
  const eph = await generatePreviewEphemeral()
  const raw = await o.open({
    url: o.url,
    browserPublic: toBase64Url(eph.publicRaw),
    ...(o.runId ? { runId: o.runId } : {}),
    ...(o.projectId ? { projectId: o.projectId } : {}),
  })
  const r = PreviewOpenResult.parse(raw)
  if (r.status !== 'open') return r
  const daemonPublic = fromBase64Url(r.daemonPublic)
  const keys = await derivePreviewTunnelKeys({
    role: 'browser',
    privateKey: eph.privateKey,
    peerPublic: daemonPublic,
    context: {
      accountId: o.accountId,
      machineId: o.machineId,
      previewId: r.previewId,
      label: r.label,
      browserPublic: eph.publicRaw,
      daemonPublic,
    },
  })
  const host =
    r.target.host.includes(':') && !r.target.host.startsWith('[')
      ? `[${r.target.host}]`
      : r.target.host
  return {
    status: 'open',
    session: {
      previewId: r.previewId,
      label: r.label,
      origin: `${o.host.scheme}//${r.label}${o.host.suffix}`,
      keys,
      entry: entryOf(o.url),
      target: { origin: `http://${host}:${r.target.port}`, port: r.target.port },
      machineLabel: r.machineLabel,
      access: r.access,
      expiresAt: r.expiresAt,
    },
  }
}

export interface PreviewRelay {
  send(frame: { type: 'preview.frame'; machineId: string; previewId: string; f: string }): boolean
  subscribe(
    cb: (f: { type: string; previewId: string; f?: string; reason?: string }) => void,
  ): () => void
}

export type FrameEvent =
  | { type: 'live' }
  | { type: 'ended'; reason: string }
  | { type: 'location'; path: string }

/**
 * Wire an iframe (already pointed at `${session.origin}/__viko/host`) to a session. Call
 * `rekey(next)` to move the same frame to a renewed session (same origin label) without reloading
 * the page. Returns a detach function.
 */
export function attachPreviewFrame(o: {
  iframe: HTMLIFrameElement
  session: OpenedPreview
  machineId: string
  relay: PreviewRelay
  onEvent: (e: FrameEvent) => void
  win?: Window
}): { detach(): void; rekey(next: OpenedPreview): void } {
  const win = o.win ?? window
  let session = o.session
  let port: MessagePort | undefined
  let unsubscribe: (() => void) | undefined
  let ready = false

  const connect = () => {
    const target = o.iframe.contentWindow
    if (!target) return
    port?.close()
    unsubscribe?.()
    const ch = new MessageChannel()
    port = ch.port1
    const s = session
    port.onmessage = (e) => {
      const d = e.data as { f?: ArrayBuffer | Uint8Array }
      if (!d?.f) return
      o.relay.send({
        type: 'preview.frame',
        machineId: o.machineId,
        previewId: s.previewId,
        f: toBase64Url(new Uint8Array(d.f)),
      })
    }
    const p = port
    unsubscribe = o.relay.subscribe((f) => {
      if (f.previewId !== s.previewId) return
      if (f.type === 'preview.frame' && f.f) {
        let bytes: Uint8Array
        try {
          bytes = fromBase64Url(f.f)
        } catch {
          return
        }
        p.postMessage({ f: bytes }, [bytes.buffer])
      } else if (f.type === 'preview.end') p.postMessage({ end: f.reason ?? 'closed' })
    })
    // Exact origin: the keys can only ever be delivered to this preview's own origin.
    target.postMessage(
      {
        type: 'viko-preview-init',
        previewId: s.previewId,
        keys: s.keys,
        entry: s.entry,
        target: s.target,
      },
      s.origin,
      [ch.port2],
    )
  }

  const onMessage = (e: MessageEvent) => {
    if (e.source !== o.iframe.contentWindow || e.origin !== session.origin) return
    const d = e.data as { type?: string; reason?: unknown; path?: unknown } | null
    if (d?.type === 'viko-preview-ready') {
      ready = true
      connect()
    } else if (d?.type === 'viko-preview-live') o.onEvent({ type: 'live' })
    else if (d?.type === 'viko-preview-ended')
      o.onEvent({ type: 'ended', reason: String(d.reason ?? 'closed') })
    else if (d?.type === 'viko-preview-location' && typeof d.path === 'string')
      o.onEvent({ type: 'location', path: d.path })
  }
  win.addEventListener('message', onMessage)
  return {
    detach() {
      win.removeEventListener('message', onMessage)
      unsubscribe?.()
      port?.close()
    },
    rekey(next) {
      if (next.origin !== session.origin) throw new Error('A renewed preview must keep its origin')
      session = next
      if (ready) connect()
    },
  }
}
